import type {
  ComplianceState,
  DomainBillingState,
  DossierStatus,
  LifecycleState,
  PolicyOutcome,
  RegistryCommandState,
  ServiceState,
} from '../../api';
import { useTranslation } from 'react-i18next';
import { Badge, type Tone } from '../../components/Badge';
import { humanizeCode } from '../../lib/labels';

export type Meta = { label: string; tone: Tone };

/** Lifecycle states of `domain.domains.lifecycle_state`. */
export const LIFECYCLE: Record<LifecycleState, Meta> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  AWAITING_DOSSIER: { label: 'Awaiting dossier', tone: 'pending' },
  AWAITING_PAYMENT: { label: 'Awaiting payment', tone: 'pending' },
  PENDING_CREATE: { label: 'Pending create', tone: 'pending' },
  CREATE_FAILED: { label: 'Create failed', tone: 'crit' },
  ACTIVE: { label: 'Active', tone: 'ok' },
  PENDING_RENEW: { label: 'Pending renew', tone: 'pending' },
  PENDING_TRANSFER: { label: 'Pending transfer', tone: 'pending' },
  EXPIRED: { label: 'Expired', tone: 'serious' },
  GRACE_PERIOD: { label: 'Grace period', tone: 'warn' },
  REDEMPTION: { label: 'Redemption', tone: 'crit' },
  PENDING_DELETE: { label: 'Pending delete', tone: 'crit' },
  DELETED: { label: 'Deleted', tone: 'neutral' },
  TRANSFERRED_OUT: { label: 'Transferred out', tone: 'neutral' },
};

/** Canonical lifecycle path, used by the Lifecycle tab. */
export const LIFECYCLE_PATH: LifecycleState[] = ['PENDING_CREATE', 'ACTIVE', 'EXPIRED', 'GRACE_PERIOD', 'REDEMPTION', 'PENDING_DELETE', 'DELETED'];

export const COMPLIANCE: Record<ComplianceState, Meta> = {
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
  COMPLIANT: { label: 'Compliant', tone: 'ok' },
  ACTION_REQUIRED: { label: 'Action required', tone: 'warn' },
  BLOCKED: { label: 'Blocked', tone: 'crit' },
  SUSPENDED: { label: 'Suspended', tone: 'crit' },
};

export const SERVICE: Record<ServiceState, Meta> = {
  NORMAL: { label: 'Normal', tone: 'ok' },
  DNS_SUSPENDED: { label: 'DNS suspended', tone: 'serious' },
  SUSPENDED: { label: 'Suspended', tone: 'crit' },
  LOCKED: { label: 'Locked', tone: 'warn' },
};

export const BILLING: Record<DomainBillingState, Meta> = {
  NONE: { label: 'None', tone: 'neutral' },
  AWAITING_PAYMENT: { label: 'Awaiting payment', tone: 'pending' },
  RESERVED: { label: 'Funds reserved', tone: 'pending' },
  PAID: { label: 'Paid', tone: 'ok' },
  REFUND_PENDING: { label: 'Refund pending', tone: 'pending' },
  REFUNDED: { label: 'Refunded', tone: 'neutral' },
  OVERDUE: { label: 'Overdue', tone: 'serious' },
};

export const DOSSIER: Record<DossierStatus, Meta> = {
  NOT_CREATED: { label: 'Not created', tone: 'crit' },
  COLLECTING: { label: 'Collecting', tone: 'warn' },
  PENDING_IDENTITY: { label: 'Pending identity', tone: 'pending' },
  PENDING_DOCUMENT: { label: 'Pending document', tone: 'pending' },
  PENDING_SIGNATURE: { label: 'Pending signature', tone: 'pending' },
  PENDING_REVIEW: { label: 'Pending review', tone: 'pending' },
  COMPLETE: { label: 'Complete', tone: 'ok' },
  INVALID: { label: 'Invalid', tone: 'crit' },
  EXPIRED: { label: 'Expired', tone: 'serious' },
  CORRECTION_REQUIRED: { label: 'Correction required', tone: 'serious' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral' },
};

/** Registry command states (ADR-008). UNKNOWN is resolved by reconciliation, never by blind retry. */
export const COMMAND_STATE: Record<RegistryCommandState, Meta> = {
  PENDING: { label: 'Pending', tone: 'pending' },
  SENT: { label: 'Sent', tone: 'pending' },
  SUCCESS: { label: 'Success', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  UNKNOWN: { label: 'Unknown', tone: 'serious' },
  RECONCILING: { label: 'Reconciling', tone: 'serious' },
  RETRY_PENDING: { label: 'Retry pending', tone: 'warn' },
  MANUAL_REVIEW: { label: 'Manual review', tone: 'crit' },
};

export const POLICY_OUTCOME: Record<PolicyOutcome, Meta> = {
  ALLOW: { label: 'Allow', tone: 'ok' },
  ALLOW_WITH_REQUIRED_ACTIONS: { label: 'Allow with actions', tone: 'warn' },
  BLOCK: { label: 'Block', tone: 'crit' },
};

/** Workflow states (upper-case in workflow.workflows, lower-case in the ops views). */
export const WORKFLOW_STATE: Record<string, Meta> = {
  RUNNING: { label: 'Running', tone: 'pending' },
  WAITING: { label: 'Waiting', tone: 'pending' },
  COMPLETED: { label: 'Completed', tone: 'ok' },
  FAILED: { label: 'Failed', tone: 'crit' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  MANUAL_REVIEW: { label: 'Manual review', tone: 'crit' },
  COMPENSATING: { label: 'Compensating', tone: 'warn' },
};

/** Transfer and registrant change states (domain.transfers / domain.registrant_changes). */
export const TRANSFER: Record<string, Meta> = {
  REQUESTED: { label: 'Requested', tone: 'pending' },
  AUTHORIZED: { label: 'Authorised', tone: 'pending' },
  REGISTRY_PENDING: { label: 'Pending at registry', tone: 'pending' },
  APPROVED: { label: 'Approved', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  EXPIRED: { label: 'Expired', tone: 'warn' },
  COMPLETED: { label: 'Completed', tone: 'ok' },
};

/** Abuse case states, as shown on the domain's compliance tab. */
export const ABUSE: Record<string, Meta> = {
  NEW: { label: 'New', tone: 'info' },
  TRIAGED: { label: 'Triaged', tone: 'pending' },
  INVESTIGATING: { label: 'Investigating', tone: 'pending' },
  ACTION_REQUIRED: { label: 'Action required', tone: 'serious' },
  WAITING: { label: 'Waiting', tone: 'neutral' },
  RESOLVED: { label: 'Resolved', tone: 'ok' },
  CLOSED: { label: 'Closed', tone: 'neutral' },
};

/** Dispute states. */
export const DISPUTE: Record<string, Meta> = {
  OPENED: { label: 'Opened', tone: 'info' },
  LOCKED: { label: 'Locked', tone: 'warn' },
  DECIDED: { label: 'Decided', tone: 'pending' },
  IMPLEMENTED: { label: 'Implemented', tone: 'pending' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
  CLOSED: { label: 'Closed', tone: 'ok' },
};

/** A dossier's sync state with VNNIC (`vnnic.dossier_sync.status`). */
export const VNNIC_SYNC: Record<string, Meta> = {
  NOT_SYNCED: { label: 'Not synced', tone: 'neutral' },
  SYNC_PENDING: { label: 'Sync pending', tone: 'pending' },
  QUEUED: { label: 'Queued', tone: 'pending' },
  SUBMITTING: { label: 'Submitting', tone: 'pending' },
  SUBMITTED: { label: 'Submitted, awaiting VNNIC', tone: 'pending' },
  ACKNOWLEDGED: { label: 'Accepted by VNNIC', tone: 'ok' },
  REJECTED: { label: 'Rejected by VNNIC', tone: 'crit' },
  CORRECTION_REQUIRED: { label: 'VNNIC requires corrections', tone: 'serious' },
  RESUBMIT_PENDING: { label: 'Resubmission pending', tone: 'pending' },
  SYNC_ERROR: { label: 'Sync error', tone: 'crit' },
  SUPERSEDED: { label: 'Superseded by a newer dossier', tone: 'neutral' },
};

/** One sync attempt (`vnnic.sync_transactions.status`). */
export const VNNIC_TX: Record<string, Meta> = {
  SUBMITTING: { label: 'Submitting', tone: 'pending' },
  SUBMITTED: { label: 'Submitted', tone: 'pending' },
  ACKNOWLEDGED: { label: 'Accepted', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  CORRECTION_REQUIRED: { label: 'Corrections required', tone: 'serious' },
  FAILED: { label: 'Failed', tone: 'crit' },
  UNKNOWN: { label: 'Outcome unknown', tone: 'serious' },
};

/** Vocabularies with translated labels in the `domains` namespace (`status.<vocab>.<CODE>`). */
const VOCAB = new WeakMap<object, string>([
  [LIFECYCLE, 'lifecycle'],
  [COMPLIANCE, 'compliance'],
  [SERVICE, 'service'],
  [BILLING, 'billing'],
  [DOSSIER, 'dossier'],
  [COMMAND_STATE, 'command'],
  [POLICY_OUTCOME, 'policy'],
  [WORKFLOW_STATE, 'workflow'],
  [TRANSFER, 'transfer'],
  [ABUSE, 'abuse'],
  [DISPUTE, 'dispute'],
  [VNNIC_SYNC, 'vnnicSync'],
  [VNNIC_TX, 'vnnicTx'],
]);

type Lookup = (key: string, options: { defaultValue: string }) => string;

/**
 * Translated label for a status code of one of the vocabularies above. Maps from other
 * areas (not registered here) fall back to their own `label`.
 */
export function useStatusLabel(): (map: Readonly<Record<string, Meta>>, value: string) => string {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  return (map, value) => {
    const code = map[value] ? value : value.toUpperCase();
    const meta = map[code];
    const vocab = VOCAB.get(map);
    if (!meta) return humanize(value);
    return vocab ? tr(`status.${vocab}.${code}`, { defaultValue: meta.label }) : meta.label;
  };
}

/** Title-cases an unknown enum value: `PENDING_REVIEW` -> `Pending review`. */
export function humanize(value: string): string {
  return humanizeCode(value);
}

/**
 * Status badge for a vocabulary map. Values the map does not know (the backend
 * stores several states as free text) render as a neutral badge with the raw
 * value humanised, never as a crash.
 */
export function StatusBadge({ map, value }: { map: Readonly<Record<string, Meta>>; value: string | null | undefined }) {
  const label = useStatusLabel();
  const { t } = useTranslation('domains');
  if (!value) return <Badge tone="neutral">—</Badge>;
  const meta = map[value] ?? map[value.toUpperCase()];
  return (
    <Badge tone={meta?.tone ?? 'neutral'} title={t('status.code', { code: value })}>
      {label(map, value)}
    </Badge>
  );
}
